import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthFlowsEnum } from '@finxone-platform/form-action';
import {
  CardSensitiveDetailsSession,
  CardInfoType,
  CardLimitsType,
  CardTransactionDetail,
  CardTransactionInfoType,
  CardTransactionRequest,
  ChangePinRequest,
  CustomFreezeResponse,
  PaginatedResponse,
  ReplaceCardRequest,
  SORT_SLUG,
  SecureCardInfoResponse,
  SecureCardTokenInfo,
  AddCardRequest,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, Subject, catchError, map, tap, throwError } from 'rxjs';
import { buildQueryString } from '../../../lib/utils/query-params.utils';
import { setConfirmationToken } from '../../utils/utils';
import { ConfigService } from '../config-service/config-service.service';

@Injectable({ providedIn: 'root' })
export class CardManagementService {
  private baseUrl = '';
  public otpCode = '';
  public originUrl = '';
  public sendDataToBottomPopup: Subject<{
    popupIndex: number;
    isFullPagePopup: boolean;
  }> = new Subject();
  private _cvvDetails: SecureCardInfoResponse;
  private _sensitiveDetailsSession?: CardSensitiveDetailsSession;
  public isActiveCardFlipped: boolean;
  constructor(private http: HttpClient, private configService: ConfigService, private store: Store) {
    this.configService.getApi('account_service').subscribe((response) => {
      this.baseUrl = response;
    });
  }

  public get cvvDetails(): SecureCardInfoResponse {
    return this._cvvDetails;
  }

  public get sensitiveDetailsSession(): CardSensitiveDetailsSession | undefined {
    return this._sensitiveDetailsSession;
  }

  getCards(
    page = 1,
    size = 20,
    sortOrder = SORT_SLUG.DESC,
    sortKey = 'createdDate',
    // GetCardsOptions filters the native GET /cards accepts. Appended only when
    // set, so callers that never pass them produce byte-for-byte the same URL.
    filters?: { name?: string; type?: 'virtual' | 'physical'; status?: string },
  ): Observable<PaginatedResponse<CardInfoType>> {
    let filterQuery = '';
    for (const [key, value] of Object.entries(filters ?? {})) {
      if (value !== undefined && value !== null && value !== '') {
        filterQuery += `&${key}=${encodeURIComponent(value)}`;
      }
    }
    return this.http
      .get<PaginatedResponse<CardInfoType>>(
        `${this.baseUrl}/cards?page=${page}&size=${size}&sort=${sortOrder}&sortKey=${sortKey}${filterQuery}`,
      )
      .pipe(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        catchError<any, Observable<boolean>>((_err) => {
          console.error(`Error getting account cards: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  getCardManagementKyc(): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/cards/check-approval-status`).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catchError<any, Observable<boolean>>((_err) => {
        console.error(`Error getting card management kyc: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  freezeCard(request: { accountId?: string; cardId: string }) {
    return this.http.post<CustomFreezeResponse>(this.baseUrl + '/cards/freeze', request).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catchError((error: any): never => {
        console.error(`Error trying freezeCard: ${JSON.stringify(error)}`);
        throw error;
      }),
    );
  }

  unfreezeCard(request: { accountId?: string; cardId: string }) {
    return this.http.post<CustomFreezeResponse>(this.baseUrl + '/cards/unfreeze', request).pipe(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catchError((error: any): never => {
        console.error(`Error trying unfreezeCard: ${JSON.stringify(error)}`);
        throw error;
      }),
    );
  }

  replaceCard(payload: ReplaceCardRequest) {
    return this.http.post<CardInfoType>(this.baseUrl + '/cards/replace-card', payload).pipe(
      catchError((_err) => {
        console.error(`Error fetching replaceCard: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  getCardSecureToken(payload: {
    publicKey: string;
    tokenPurpose: string;
    accountId: string;
    cardId: string;
  }) {
    return this.http.post<SecureCardTokenInfo>(this.baseUrl + '/cards/secure-token', payload).pipe(
      catchError<SecureCardTokenInfo, Observable<SecureCardTokenInfo>>((_err) => {
        console.error(`Error fetching getCardSecureToken: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  changePin(payload: ChangePinRequest) {
    return this.http.post<boolean>(this.baseUrl + '/cards/change-pin', payload).pipe(
      catchError<boolean, Observable<boolean>>((_err) => {
        console.error(`Error fetching changePin: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  activateCard(payload: { accountId: string; cardId: string }, accessToken?: string) {
    let confirmationToken = setConfirmationToken(this.store, AuthFlowsEnum.CARDS);
    if (accessToken) {
      confirmationToken = accessToken;
    }
    const headers = new HttpHeaders({
      confirmation: confirmationToken,
    });
    return this.http.post<boolean>(this.baseUrl + '/cards/activate', payload, { headers }).pipe(
      catchError<boolean, Observable<boolean>>((_err) => {
        console.error(`Error fetching activateCard: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  getCVVDetails(payload: { accountId?: string; cardId: string; secureToken?: string }, accessToken: string) {
    const headers = new HttpHeaders({
      confirmation: accessToken,
    });
    return this.http
      .post<SecureCardInfoResponse>(this.baseUrl + '/cards/cvv-details', payload, { headers })
      .pipe(
        tap((response: SecureCardInfoResponse) => {
          this._cvvDetails = response;
        }),
        catchError((_err) => {
          console.error(`Error fetching getCVVDetails: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  getSensitiveDetailsSession(
    payload: { accountId?: string; cardId: string; secureToken?: string },
    accessToken: string,
  ) {
    const headers = new HttpHeaders({
      confirmation: accessToken,
    });
    return this.http
      .post<CardSensitiveDetailsSession>(this.baseUrl + '/cards/sensitive-details/session', payload, {
        headers,
      })
      .pipe(
        tap((response) => {
          this._sensitiveDetailsSession = response;
          if (response.mode === 'backend-values' && response.secureDetails) {
            this._cvvDetails = {
              pan: response.secureDetails.pan ?? '',
              cvv2: response.secureDetails.cvv2 ?? '',
              expDate: response.secureDetails.expDate ?? '',
            };
          }
        }),
        catchError((_err) => {
          console.error(`Error fetching getSensitiveDetailsSession: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  getSecureToken(payload: { cardId: string; publicKey?: string; tokenPurpose?: string }) {
    return this.http
      .post<SecureCardTokenInfo>(this.baseUrl + '/cards/secure-token', {
        cardId: payload.cardId,
        publicKey: payload.publicKey ?? '',
        tokenPurpose: payload.tokenPurpose ?? 'read',
      })
      .pipe(
        catchError((_err) => {
          console.error(`Error fetching getSecureToken: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  viewCardPin(payload: { cardId: string }, accessToken: string) {
    const headers = new HttpHeaders({
      confirmation: accessToken,
    });
    return this.http.post<{ pin: string }>(this.baseUrl + '/cards/view-pin', payload, { headers }).pipe(
      catchError((_err) => {
        console.error(`Error fetching viewCardPin: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  getCardDetails(payload: { accountId: string; cardId: string }) {
    return this.http
      .get<CardInfoType>(this.baseUrl + '/cards/details', {
        params: payload,
      })
      .pipe(
        catchError<CardInfoType, Observable<CardInfoType>>((_err) => {
          console.error(`Error fetching getCardDetails: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public getCardLimits(cardId: string) {
    return this.http.get<CardLimitsType[]>(this.baseUrl + `/cards/${cardId}/limits`).pipe(
      catchError<CardLimitsType[], Observable<CardLimitsType[]>>((_err) => {
        console.error(`Error fetching getCardLimits: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  createVirtualCard(accountId: string): Observable<string> {
    const request = {
      accountId: [accountId],
      cardType: 'virtual',
    };
    return this.http.post(this.baseUrl + '/cards', request).pipe(
      map(() => accountId),
      catchError((_err) => {
        console.error(`Error fetching createVirtualCard: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  createPhysicalCard(accountId: string): Observable<string> {
    const request = {
      accountId: [accountId],
      cardType: 'physical',
    };
    return this.http.post(this.baseUrl + '/cards', request).pipe(
      map(() => accountId),
      catchError((_err) => {
        console.error(`Error fetching createPhysicalCard: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  deleteVirtualCard(payload: { cardId: string; accountId: string }): Observable<string> {
    return this.http.delete(`${this.baseUrl}/cards/${payload.cardId}`).pipe(
      map(() => payload.accountId),
      catchError((_err) => {
        console.error(`Error fetching deleteVirtualCard: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  cancelCard(cardId: string, accessToken?: string): Observable<string> {
    let confirmationToken = setConfirmationToken(this.store, AuthFlowsEnum.CARDS);
    if (accessToken) {
      confirmationToken = accessToken;
    }
    const headers = new HttpHeaders({
      confirmation: confirmationToken,
    });
    return this.http.delete(`${this.baseUrl}/cards/${cardId}`, { headers }).pipe(
      map(() => cardId),
      catchError((_err) => {
        console.error(`Error fetching cancelCard: ${JSON.stringify(_err)}`);
        throw _err;
      }),
    );
  }

  setCardName(cardId: string, cardName: string): Observable<string> {
    const url = `${this.baseUrl}/cards/${cardId}/name`;
    const requestPayload = { nickname: cardName };
    return this.http.patch(url, requestPayload).pipe(
      map(() => cardId),
      catchError((error) => {
        console.error(`Error in setCardName:`, error);
        return throwError(() => error);
      }),
    );
  }

  public getcCardTransactionDetailById(cardId: string, transactionId: string) {
    return this.http
      .get<CardTransactionDetail>(`${this.baseUrl}/cards/${cardId}/transactions/${transactionId}`)
      .pipe(
      catchError((error) => {
        console.error(`Error getting card transaction detail:`, error);
        return throwError(() => error);
      }),
    );
  }

  viewEPIN(cardId: string, accessToken: string) {
    const headers = new HttpHeaders({
      confirmation: accessToken,
    });
    return this.http.get<{ epin: string }>(`${this.baseUrl}/cards/${cardId}/epin`, { headers }).pipe(
      map((ePinResult) => ePinResult?.epin ?? ''),
      catchError((error) => {
        console.error(`Error fetching viewEPIN: ${JSON.stringify(error)}`);
        return throwError(() => error);
      }),
    );
  }

  public getCardTransactions(
    cardId: string,
    params: CardTransactionRequest,
  ): Observable<PaginatedResponse<CardTransactionInfoType>> {
    const queryString = buildQueryString(params);

    return this.http
      .get<PaginatedResponse<CardTransactionInfoType>>(
        `${this.baseUrl}/cards/${cardId}/transactions${queryString}`,
      )
      .pipe(
        catchError<any, Observable<boolean>>((_err) => {
          console.error(`Error getting card transactions: ${JSON.stringify(_err)}`);
          throw _err;
        }),
      );
  }

  public addCard(payload: AddCardRequest, accessToken?: string) {
    let confirmationToken = setConfirmationToken(this.store, AuthFlowsEnum.CARDS);
    if (accessToken) {
      confirmationToken = accessToken;
    }
    const headers = new HttpHeaders({
      confirmation: confirmationToken,
    });
    return this.http
      .post<AddCardRequest>(`${this.baseUrl}/cards`, payload, {
        headers,
      })
      .pipe(
        catchError((error: any): never => {
          console.error(`Error trying addCard: ${JSON.stringify(error)}`);
          throw error;
        }),
      );
  }
}
